import { useEffect, useMemo, useState } from "react";
import { Bell, Sparkles, ChevronRight, Sun, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import { api, MARKET_ORDER, MARKET_META } from "../lib/api";
import { MarketCard } from "../components/MarketCard";
import { HomeSeoContent } from "../components/HomeSeoContent";
import { useSeo } from "../lib/useSeo";

// Teer gaming day: rolls over at 1 AM IST so late-night results stay on same day
const todayISTString = () => new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

// Big hero date in DD-MM-YYYY format (auto rolls over at 1 AM IST)
const heroDateString = () => {
    const iso = new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
};

const prettyDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const Home = () => {
    useSeo({
        title: "Shillong Morning Teer Result Today – Live FR & SR Results",
        description: "Check today’s Shillong Morning Teer Result, live FR and SR updates, previous results, common numbers and daily information.",
        keywords: "Shillong Morning Teer, Morning Teer Result Today, Shillong Teer Result, FR Result, SR Result, Morning Teer, Meghalaya Teer",
        path: "/",
        ogTitle: "Shillong Morning Teer Result Today – Live Results",
        ogDescription: "Check Shillong Morning Teer Live Results, FR & SR Results, Previous Results, Common Number and Daily Updates.",
        image: "https://shillongmorningteerresulttoday.com/og-image.png",
        imageAlt: "Shillong Morning Teer Result Today – Live FR and SR Results at shillongmorningteerresulttoday.com",
        breadcrumbs: [{ name: "Home", path: "/" }],
    });

    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("morning"); // morning | night
    const [now, setNow] = useState(new Date());

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/results/today");
            setResults(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        const c = setInterval(() => setNow(new Date()), 1000);
        return () => { clearInterval(t); clearInterval(c); };
    }, []);

    const byKey = useMemo(() => Object.fromEntries(results.map(r => [r.market_key, r])), [results]);

    // Show ALL 8 markets together on home — no morning/night tabs
    const activeKeys = MARKET_ORDER;

    const dateStr = todayISTString();
    const heroDate = heroDateString();

    return (
        <div className="pb-8">
            {/* Marquee band */}
            <div className="bg-yellow-400 text-blue-950 border-y border-yellow-500/50 overflow-hidden">
                <div className="marquee-track whitespace-nowrap py-2 font-semibold text-sm">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <span key={i} className="mx-8 inline-flex items-center gap-2">
                            <Bell className="h-4 w-4" />
                            Shillong Morning Teer Result Today — Sabse fast updates har market ke liye.
                            <Sparkles className="h-4 w-4" />
                            Common Number · Dream Number · Direct Hit Guti
                            <Bell className="h-4 w-4" />
                            Har din live results — 10:10 AM se raat 12:25 AM tak.
                        </span>
                    ))}
                </div>
            </div>

            {/* Hero — 2:1 aspect banner with big auto-updating Teer date */}
            <section className="relative bg-blue-900 text-white overflow-hidden" data-testid="hero-section">
                <div className="max-w-4xl mx-auto px-4 md:px-8 pt-5 pb-6 md:pt-6 md:pb-8">
                    <div
                        className="relative w-full rounded-2xl overflow-hidden shadow-xl border border-white/10 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950"
                        style={{ aspectRatio: "2 / 1" }}
                        data-testid="hero-date-container"
                    >
                        {/* Subtle grain / dot pattern for depth */}
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 opacity-30"
                            style={{
                                backgroundImage:
                                    "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.06) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(250,204,21,0.10) 0%, transparent 45%)",
                            }}
                        />

                        <div className="absolute inset-0 grid place-items-center text-center px-4">
                            <div className="flex flex-col items-center">
                                {/* Live pill */}
                                <div
                                    className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] md:text-[11px] font-bold uppercase tracking-widest shadow-lg"
                                    data-testid="hero-live-pill"
                                >
                                    <span className="h-1.5 w-1.5 rounded-full bg-white live-dot" /> Live Result
                                </div>

                                {/* Label */}
                                <div className="mt-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-yellow-300 font-semibold">
                                    Today's Teer Date
                                </div>

                                {/* Big DD-MM-YYYY date — kept large */}
                                <div
                                    className="mt-2 md:mt-3 inline-block px-5 md:px-10 py-2 md:py-3 rounded-2xl bg-yellow-400 text-blue-950 font-num font-black text-4xl sm:text-6xl md:text-7xl tracking-tight shadow-2xl ring-4 ring-yellow-300/50 leading-none"
                                    data-testid="hero-date-badge"
                                >
                                    {heroDate}
                                </div>

                                {/* Tagline */}
                                <div className="mt-2 md:mt-3 text-[11px] md:text-sm text-blue-100/90 max-w-2xl">
                                    Shillong Teer · Morning &amp; Day &amp; Night · All Live Results in one place
                                </div>

                                {/* Powered-by chip */}
                                <div className="mt-1.5 md:mt-2 text-[9px] md:text-[10px] uppercase tracking-widest text-yellow-300/90 font-semibold">
                                    Powered by Khasi Hills Archery Sports Institute
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Result section */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6 md:mt-8 relative z-10" data-testid="results-section">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                    {loading && activeKeys.map((k) => (
                        <div key={k} className="rounded-lg h-32 skeleton" />
                    ))}
                    {!loading && activeKeys.map((k) => (
                        <MarketCard
                            key={k}
                            marketKey={k}
                            result={byKey[k]}
                            date={dateStr}
                            onRefresh={load}
                        />
                    ))}
                </div>

                {/* SEO content sections (H2 headings, chart, about, FAQ) */}
                <HomeSeoContent />
            </section>
        </div>
    );
};

const Stat = ({ icon, label, value }) => (
    <div className="p-3 rounded-xl bg-white/5 border border-white/10">
        <div className="text-yellow-300">{icon}</div>
        <div className="text-xs text-blue-100/70 mt-1">{label}</div>
        <div className="font-display font-bold text-white text-base tracking-tight">{value}</div>
    </div>
);

const BigNumber = ({ label, value, testid }) => {
    const pending = value === null || value === undefined || value === "";
    const text = pending ? "XX" : String(value).padStart(2, "0");
    return (
        <div className="px-5 py-6">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div
                className={`mt-2 result-digit text-5xl md:text-6xl ${pending ? "text-slate-300" : "text-blue-900"}`}
                data-testid={testid}
            >
                {text}
            </div>
        </div>
    );
};

const CtaItem = ({ to, title, desc, accent, testid }) => (
    <Link to={to} className="p-6 flex items-center gap-4 hover:bg-slate-50 transition-colors group" data-testid={testid}>
        <div className={`h-12 w-12 rounded-xl ${accent} grid place-items-center font-display font-bold`}>
            <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1">
            <div className="font-display font-bold text-blue-950 tracking-tight">{title}</div>
            <div className="text-sm text-slate-500">{desc}</div>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-blue-900 transition-colors" />
    </Link>
);

export default Home;
